import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadMe = useCallback(async () => {
        try {
            const r = await api.get("/auth/me");
            setMe(r.data);
            return r.data;
        } catch (e) {
            setMe(null);
            return null;
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (tokenStore.getAccess()) {
                await loadMe();
            }
            if (mounted) setLoading(false);
        })();
        const onLogout = () => setMe(null);
        window.addEventListener("ops:logout", onLogout);
        return () => {
            mounted = false;
            window.removeEventListener("ops:logout", onLogout);
        };
    }, [loadMe]);

    const login = useCallback(
        async (identifier, password) => {
            const r = await api.post("/auth/login", { identifier, password });
            tokenStore.set(r.data.access_token, r.data.refresh_token);
            await loadMe();
            return r.data;
        },
        [loadMe]
    );

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            /* best effort */
        }
        tokenStore.clear();
        setMe(null);
    }, []);

    const user = me?.user;
    const isSuperAdmin = user?.role === "super_admin";
    const isInspector = user?.role === "inspector";
    const isBranchManager = user?.role === "branch_manager";
    const isOpsManager = user?.role === "ops_manager";

    // Capability model: super_admin holds everything; everyone else is driven
    // purely by their stored tabs + caps.
    // A master toggle (`qc_full_control`, `cctv_full_control`) IMPLIES its
    // module's tabs + caps — mirrors registry.MASTER_CAP_IMPLICATIONS on the
    // server — so every gate below keeps reading a single flat list.
    const perms = user?.permissions || {};
    const storedTabs = perms.tabs || [];
    const storedCaps = perms.caps || [];
    const MASTER_IMPLIES = {
        qc_full_control: {
            tabs: ["qc_inspection", "qc_waste"],
            caps: ["qc_all_branches", "qc_submit", "qc_approve", "qc_sections",
                "qc_violation_manage", "user_admin"],
        },
        cctv_full_control: {
            tabs: ["cctv_monitoring"],
            caps: ["cctv_submit", "cctv_dispatch", "user_admin"],
        },
        // Branch hub master (Cancellations CP1): exactly the eight module keys
        // and the hub tab — mirrors registry.MASTER_CAP_IMPLICATIONS, asserted
        // by tests/test_branch_ops_master_containment.py. (No Waste & Meals
        // entry on purpose: TabRoute reads me.nav[].permitted from the server.)
        branch_ops_full_control: {
            tabs: ["waste_meals"],
            caps: ["wm_submit", "wm_branch_manage", "wm_admin",
                "co_submit", "co_submit_all_branches", "co_branch_manage", "co_review", "co_admin"],
        },
        // «التحليل الذكي» (2026-10-18): the cap opens its own tab, nothing else —
        // mirrors registry.MASTER_CAP_IMPLICATIONS. Advice stays super-admin only.
        ai_analysis_view: { tabs: ["ai_analysis"], caps: [] },
        // «الصيانة» (2026-10-31): the same shape — its own tab, nothing else.
        maintenance_view: { tabs: ["maintenance"], caps: [] },
        // «متوسط فاتورة الكاشير» (2026-11-02): the same shape — its own tab, nothing else.
        cashier_check_view: { tabs: ["cashier_check"], caps: [] },
    };
    const hasQcFullControl = storedCaps.includes("qc_full_control");
    const hasCctvFullControl = storedCaps.includes("cctv_full_control");
    const impliedTabs = [];
    const impliedCaps = [];
    Object.entries(MASTER_IMPLIES).forEach(([master, imp]) => {
        if (storedCaps.includes(master)) {
            impliedTabs.push(...imp.tabs);
            impliedCaps.push(...imp.caps);
        }
    });
    const tabs = [...new Set([...storedTabs, ...impliedTabs])];
    const caps = [...new Set([...storedCaps, ...impliedCaps])];
    const hasTab = (k) => isSuperAdmin || tabs.includes(k);
    const hasCap = (k) => isSuperAdmin || caps.includes(k);

    const isUserAdmin = isSuperAdmin || caps.includes("user_admin");
    const isSystemAdmin = isSuperAdmin || caps.includes("infrastructure");
    const canOpsDashboard = isSuperAdmin || caps.includes("ops_dashboard");
    const canSalesBudget = isSuperAdmin || caps.includes("sales_budget");
    const canBranchCheckin = isSuperAdmin || caps.includes("branch_checkin") || tabs.includes("cctv_monitoring");
    const canBranchKpi = isSuperAdmin || caps.includes("branch_kpi");
    const canApproveQc = isSuperAdmin || caps.includes("qc_approve");
    const canEditQcSections = isSuperAdmin || caps.includes("qc_sections");
    const qcAllBranches = isSuperAdmin || caps.includes("qc_all_branches");
    const canCctvManage = isSuperAdmin || caps.includes("cctv_manage");
    const canQcViolationManage = isSuperAdmin || caps.includes("qc_violation_manage");
    // Round administration: edit meta / reopen / force-submit / archive /
    // unarchive + the archived list. Permanent delete stays super-admin only.
    const canQcRoundAdmin = isSuperAdmin || hasQcFullControl;
    // Evidence / photo hard-delete is super-admin only in BOTH modules.
    const canDeleteEvidence = isSuperAdmin;
    const canCctvFullControl = isSuperAdmin || hasCctvFullControl;
    const canAiAnalysis = isSuperAdmin || caps.includes("ai_analysis_view");
    const canMaintenance = isSuperAdmin || caps.includes("maintenance_view");

    return (
        <AuthContext.Provider
            value={{
                me, loading, login, logout, refreshMe: loadMe,
                tabs, caps, hasTab, hasCap,
                isSuperAdmin, isInspector, isBranchManager, isOpsManager,
                isUserAdmin, isSystemAdmin,
                canOpsDashboard, canSalesBudget, canBranchCheckin, canBranchKpi,
                canApproveQc, canEditQcSections, qcAllBranches,
                canCctvManage, canQcViolationManage, canQcRoundAdmin,
                canCctvFullControl, canDeleteEvidence, canAiAnalysis, canMaintenance,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
